import React, { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route } from "react-router-dom"
import { Company, LoggedUser, UserConfigs } from '../db/db'
import { Equipment } from '../models/equipment';
import CookbookModel from '../models/cookbook';

import Layout from '../pages/Layout';
import RouteMonitor from './routeMonitor';
import ProtectedRoute from './authentication/protectedRoute';
import SerialNumbers from '../pages/SerialNumbers';
import GroupLocationDashboard from '../pages/Dashboards/GroupLocationDashboard';
import { ThemeProvider } from '../context/ThemeContext';
import { ChatRoute } from './ChatRoute';
import { Error404 } from '../pages/Error404';
import ZendeskSupportContextBridge from '../integrations/ZendeskSupportContextBridge';

const LoginPage = lazy(() => import('../pages/Login'));
const MaintenancePage = lazy(() => import('../pages/Maintenance'));
const HomePage = lazy(() => import('../pages/Home'));
const HomeSupportPage = lazy(() => import('../pages/HomeSupport'));
const HomeMasterPage = lazy(() => import('../pages/Home/Master'));
const RegisterPage = lazy(() => import('../pages/Register'));
const ActivatePage = lazy(() => import('../pages/Activate'));
const Menus = lazy(() => import('../pages/Menus'));
const Menu = lazy(() => import('../pages/Menu'));
const CookbookPage = lazy(() => import('../pages/Cookbook'));
const EquipmentPage = lazy(() => import('../pages/Equipment'));
const UsersPage = lazy(() => import('../pages/Users'));
const CallsPage = lazy(() => import('../pages/Calls'));
const Group = lazy(() => import('../pages/Group'));
const MenuSimpleRecipe = lazy(() => import('../pages/MenuSimpleRecipe'));
const CreateRecipe = lazy(() => import('../pages/CreateRecipe'));
const Recipe = lazy(() => import('../pages/Recipe'));
const EditRecipe = lazy(() => import('../pages/EditRecipe'));
const CookbookRecipe = lazy(() => import('../pages/CookbookRecipe'));
const CreateCookbook = lazy(() => import('../pages/CreateCookbook'));
const EditCookbook = lazy(() => import('../pages/EditCookbook'));
const RecoverPasswordPage = lazy(() => import('../pages/RecoverPassword'));
const PasswordResetPage = lazy(() => import('../pages/PasswordReset'));
const CreateSimpleRecipe = lazy(() => import('../pages/CreateSimpleRecipe'));
const SimpleRecipe = lazy(() => import('../pages/SimpleRecipe'));
const EditSimpleRecipe = lazy(() => import('../pages/EditSimpleRecipe'));
const EquipmentDetails = lazy(() => import('../pages/EquipmentDetails'));
const EntityPage = lazy(() => import('../pages/EntityPage'));
const RecoverAccountPage2 = lazy(() => import('../pages/RecoverAccount2'));
const ActivateRecoverUserPage = lazy(() => import('../pages/Activate-recover-user'));
const DeleteRecuseUserPage = lazy(() => import('../pages/Recuse-user'));
const DeveloperTokenResetPage = lazy(() => import('../pages/RecuseDeveloperToken'));
const AnalyticsPage = lazy(() => import('../pages/Analytics'));
const MergeAccountsPage = lazy(() => import('../pages/MergeAccounts'));
const CompaniesPage = lazy(() => import('../pages/Companies'));
const EquipmentMaintenance = lazy(() => import('../pages/EquipmentMaintenance'));
const IOKInformationPage = lazy(() => import('../pages/IOKInformation'));
const HACCPPage = lazy(() => import('../pages/HACCP/index'));
const ChatPage = lazy(() => import('../pages/Chat'));
interface Props {
	user?: LoggedUser[]
	company?: Company[]
	userConfigs?: UserConfigs[]
	cookbook?: CookbookModel[]
	equipment?: Equipment[]
}

const AppRoutes = ({ user, userConfigs, company, cookbook, equipment }: Props) => {
	return (
		<BrowserRouter>
			<ZendeskSupportContextBridge />
			<ThemeProvider>
				<Routes>
					<Route path="/" element={<RouteMonitor><Layout user={user} configs={userConfigs} company={company} /></RouteMonitor>}>
						<Route path='maintenance' element={<Suspense fallback={<div>Loading...</div>}><MaintenancePage /></Suspense>} />
						<Route path='login' element={<Suspense fallback={<div>Loading...</div>}><LoginPage /></Suspense>} />
						<Route path='recoverPassword' element={<Suspense fallback={<div>Loading...</div>}><RecoverPasswordPage /></Suspense>} />
						<Route path='resetPassword' element={<Suspense fallback={<div>Loading...</div>}><PasswordResetPage /></Suspense>} />
						<Route path='resetDeveloperToken' element={<Suspense fallback={<div>Loading...</div>}><DeveloperTokenResetPage user={user} /></Suspense>} />
						<Route path='teste' element={<ProtectedRoute element={<Suspense fallback={<div>Loading...</div>}><HomePage /></Suspense>} />} />
						<Route path='home' element={<ProtectedRoute element={<Suspense fallback={<div>Loading...</div>}><HomePage user={user} userConfigs={userConfigs} /></Suspense>} />} />
						<Route path='home/support' element={<ProtectedRoute element={<Suspense fallback={<div>Loading...</div>}><HomeSupportPage user={user} userConfigs={userConfigs} /></Suspense>} />} />
						<Route path='home/master' element={<ProtectedRoute element={<Suspense fallback={<div>Loading...</div>}><HomeMasterPage user={user} userConfigs={userConfigs} /></Suspense>} />} />
						<Route
							path='chat'
							element={
								<ProtectedRoute
									element={
										<ChatRoute companyType={company?.[0]?.companyType}>
											<Suspense fallback={null}>
												<ChatPage />
											</Suspense>
										</ChatRoute>
									}
								/>
							}
						/>
						<Route path='*' element={<Suspense fallback={<div>Loading...</div>}><Error404 /></Suspense>} />
						<Route path='register' element={<Suspense fallback={<div>Loading...</div>}><RegisterPage userConfigsDB={userConfigs && userConfigs[0]} /></Suspense>} />
						<Route path='activate' element={<Suspense fallback={<div>Loading...</div>}><ActivatePage /></Suspense>} />
						<Route path='activate/recover/user' element={<Suspense fallback={<div>Loading...</div>}><ActivateRecoverUserPage /></Suspense>} />
						<Route path='recuse/user' element={<Suspense fallback={<div>Loading...</div>}><DeleteRecuseUserPage /></Suspense>} />
						<Route path='menu' element={<ProtectedRoute element={<Suspense fallback={<div>Loading...</div>}><Menus user={user} /></Suspense>} />} />
						<Route path='menu/:idMenu' element={<ProtectedRoute element={<Suspense fallback={<div>Loading...</div>}><Menu user={user} /></Suspense>} />} />
						<Route path='menu/simple/:idMenu' element={<ProtectedRoute element={<Suspense fallback={<div>Loading...</div>}><MenuSimpleRecipe /></Suspense>} />} />
						<Route path='menu/simple/:idMenu/recipe/:idRecipe' element={<ProtectedRoute element={<Suspense fallback={<div>Loading...</div>}><SimpleRecipe /></Suspense>} />} />
						<Route path='menu/simple/:idMenu/recipe/:idRecipe/editRecipe' element={<ProtectedRoute element={<Suspense fallback={<div>Loading...</div>}><EditSimpleRecipe /></Suspense>} />} />
						<Route path='menu/simple/:idMenu/createSimpleRecipe/:quantRecipe' element={<ProtectedRoute element={<Suspense fallback={<div>Loading...</div>}><CreateSimpleRecipe user={user} /></Suspense>} />} />
						<Route path='menu/:idMenu/group/:idGroup' element={<ProtectedRoute element={<Suspense fallback={<div>Loading...</div>}><Group user={user} /></Suspense>} />} />
						<Route path='menu/:idMenu/group/:idGroup/createRecipe/:quantRecipe' element={<ProtectedRoute element={<Suspense fallback={<div>Loading...</div>}><CreateRecipe user={user} /></Suspense>} />} />
						<Route path='menu/:idMenu/group/:idGroup/recipe/:idRecipe' element={<ProtectedRoute element={<Suspense fallback={<div>Loading...</div>}><Recipe user={user} /></Suspense>} />} />
						<Route path='menu/:idMenu/group/:idGroup/recipe/:idRecipe/editRecipe' element={<ProtectedRoute element={<Suspense fallback={<div>Loading...</div>}><EditRecipe /></Suspense>} />} />
						<Route path='cookbook' element={<ProtectedRoute element={<Suspense fallback={<div>Loading...</div>}><CookbookPage user={user} company={company} /></Suspense>} />} />
						<Route path='cookbook/company/:companyId/createCookbook' element={<ProtectedRoute element={<Suspense fallback={<div>Loading...</div>}><CreateCookbook user={user} /></Suspense>} />} />
						<Route path='cookbook/recipe/:idRecipe' element={<ProtectedRoute element={<Suspense fallback={<div>Loading...</div>}><CookbookRecipe user={user} /></Suspense>} />} />
						<Route path='cookbook/recipe/:idRecipe/update' element={<ProtectedRoute element={<Suspense fallback={<div>Loading...</div>}><EditCookbook cookbook={cookbook} /></Suspense>} />} />
						<Route path='equipment' element={<ProtectedRoute element={<Suspense fallback={<div>Loading...</div>}><EquipmentPage user={user} company={company} userConfigs={userConfigs} /></Suspense>} />} />
						<Route path='users' element={<ProtectedRoute element={<Suspense fallback={<div>Loading...</div>}><UsersPage user={user} company={company} userConfigs={userConfigs} /></Suspense>} />} />
						<Route path='analytics' element={<ProtectedRoute element={<Suspense fallback={<div>Loading...</div>}><AnalyticsPage user={user} /></Suspense>} />} />
						<Route path='calls' element={<ProtectedRoute element={<Suspense fallback={<div>Loading...</div>}><CallsPage /></Suspense>} />} />
						<Route path='equipment/:NS' element={<ProtectedRoute element={<Suspense fallback={<div>Loading...</div>}><EquipmentDetails user={user} /></Suspense>} />} />
						<Route path='equipment/:NS/haccp' element={<ProtectedRoute element={<Suspense fallback={<div>Loading...</div>}><HACCPPage /></Suspense>} />} />
						<Route path='entity' element={<Suspense fallback={<div>Loading...</div>}><EntityPage /></Suspense>} />
						<Route path='recoverAccount' element={<Suspense fallback={<div>Loading...</div>}><RecoverAccountPage2 /></Suspense>} />
						<Route path='companyMerge' element={<Suspense fallback={<div>Loading...</div>}><MergeAccountsPage /></Suspense>} />
						<Route path='companies' element={<ProtectedRoute element={<Suspense fallback={<div>Loading...</div>}><CompaniesPage /></Suspense>} />} />
						<Route path='equipment/maintenance/:NS' element={<ProtectedRoute element={<Suspense fallback={<div>Loading...</div>}><EquipmentMaintenance user={user} /></Suspense>} />} />
						<Route path='companies/:idCompany' element={<ProtectedRoute element={<Suspense fallback={<div>Loading...</div>}><SerialNumbers /></Suspense>} />} />
						<Route path='equipment/dashboard' element={<ProtectedRoute element={<Suspense fallback={<div>Loading...</div>}><GroupLocationDashboard /></Suspense>} />} />
						<Route path='information' element={<ProtectedRoute element={<Suspense fallback={<div>Loading...</div>}><IOKInformationPage /></Suspense>} />} />
					</Route>
				</Routes>
			</ThemeProvider>
		</BrowserRouter>
	)
}

export default AppRoutes;
